import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { elementDetailKeys } from '@/hooks/api/useProjectElementsDetail';
import { projectElementKeys } from '@/hooks/api/useProjectElements';
import {
  invalidateMentions,
  invalidateMentionQueriesForStoryboard,
  mentionKeys,
} from '@/hooks/api/useMentionableResources';
import {
  isTagResourcesQueryKey,
  invalidateResourceLibraryTagResources,
  type ResourceLibraryType,
} from '@/hooks/api/useResourceLibrary';
import { storyboardBoardKeys } from '@/hooks/api/useStoryboardBoard';
import {
  invalidateStoryboardDialogues,
  storyboardDialoguesKeys,
} from '@/hooks/api/useStoryboardDialogues';
import { mergeUpdatedElementTag } from '@/lib/merge-updated-element-tag';
import type { ElementType, ProjectElementTagResponse } from '@/types/project-element-tag';

export type ResourceImageTaskMode = 'character-image' | 'location-image' | 'prop-image';

const PROJECT_ELEMENT_TAGS_ROOT = 'project-element-tags';
export type QueryRefetchType = 'active' | 'none';

/** 与 projectElementTagsKeys 结构一致，避免 lib ↔ useProjectElementTags 循环依赖 */
function elementTagsProjectKey(projectId: string) {
  return [PROJECT_ELEMENT_TAGS_ROOT, projectId] as const;
}

function elementTagsElementKey(projectId: string, elementType: ElementType, elementId: string) {
  return [PROJECT_ELEMENT_TAGS_ROOT, projectId, elementType, elementId] as const;
}

function markElementDetailStale(
  queryClient: QueryClient,
  projectId: string,
  elementType: ResourceElementType,
  elementId: string
): void {
  const queryKey =
    elementType === 'character'
      ? elementDetailKeys.character(projectId, elementId)
      : elementType === 'location'
        ? elementDetailKeys.location(projectId, elementId)
        : elementDetailKeys.prop(projectId, elementId);
  void queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
}

function markProjectElementsListStale(
  queryClient: QueryClient,
  projectId: string,
  elementType: ResourceElementType,
  refetchType: QueryRefetchType = 'none'
): void {
  const queryKey =
    elementType === 'character'
      ? projectElementKeys.characters(projectId)
      : elementType === 'location'
        ? projectElementKeys.locations(projectId)
        : projectElementKeys.props(projectId);
  void queryClient.invalidateQueries({ queryKey, refetchType });
}

function markResourceLibraryStale(
  queryClient: QueryClient,
  projectId: string,
  resourceLibraryType: ResourceLibraryType,
  refetchType: QueryRefetchType = 'none'
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'resource-library' &&
        key[1] === 'v1' &&
        (key[2] === 'list' || key[2] === 'infinite') &&
        key[3] === projectId &&
        key[4] === resourceLibraryType
      );
    },
    refetchType,
  });
}

function updateTagInCacheList(
  current: ProjectElementTagResponse[] | undefined,
  result: ProjectElementTagResponse,
  options?: { insertIfMissing?: boolean }
): ProjectElementTagResponse[] | undefined {
  if (!current) return current;
  let matched = false;
  const next = current.map((tag) => {
    if (tag.tagId !== result.tagId) return tag;
    matched = true;
    return mergeUpdatedElementTag(tag, result);
  });
  if (matched) return next;
  return options?.insertIfMissing ? [...current, result] : current;
}

function patchElementTagCaches(
  queryClient: QueryClient,
  result: ProjectElementTagResponse,
  options?: { projectIds?: string[]; insertIfMissing?: boolean }
): void {
  const allowedProjectIds = options?.projectIds ? new Set(options.projectIds) : null;
  queryClient.setQueriesData<ProjectElementTagResponse[]>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === PROJECT_ELEMENT_TAGS_ROOT &&
          (allowedProjectIds === null ||
            (typeof key[1] === 'string' && allowedProjectIds.has(key[1]))) &&
          (key[2] === result.elementType || key[2] === undefined) &&
          (key[3] === undefined || key[3] === result.elementId)
        );
      },
    },
    (current) =>
      updateTagInCacheList(current, result, {
        insertIfMissing: options?.insertIfMissing,
      })
  );
}

function removeElementTagCaches(
  queryClient: QueryClient,
  params: {
    projectIds: string[];
    tagId: string;
    elementType: ElementType;
    elementId: string;
  }
): void {
  const allowedProjectIds = new Set(params.projectIds);
  queryClient.setQueriesData<ProjectElementTagResponse[]>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === PROJECT_ELEMENT_TAGS_ROOT &&
          typeof key[1] === 'string' &&
          allowedProjectIds.has(key[1]) &&
          (key[2] === params.elementType || key[2] === undefined) &&
          (key[3] === undefined || key[3] === params.elementId)
        );
      },
    },
    (current) => current?.filter((tag) => tag.tagId !== params.tagId) ?? current
  );
}

/** 查询/权限上下文 vs DB 归属（角色/场景/道具及标签入库 project_id 为父项目） */
export type ProjectResourceScope = {
  /** API path / UI 路由项目 ID（单集制作为子项目） */
  permissionProjectId: string;
  /** DB 中 tag.project_id（单集制作为父项目） */
  resourceProjectId: string;
};

export function resolveProjectResourceScope(input: {
  permissionProjectId: string;
  resourceProjectIdFromApi?: string;
}): ProjectResourceScope {
  return {
    permissionProjectId: input.permissionProjectId,
    resourceProjectId: input.resourceProjectIdFromApi ?? input.permissionProjectId,
  };
}

export function resolveElementTagMutationInvalidationScope(input: {
  mutationProjectId: string;
  permissionProjectId?: string;
  responseProjectId: string;
}): ProjectResourceScope {
  return resolveProjectResourceScope({
    permissionProjectId: input.permissionProjectId ?? input.mutationProjectId,
    resourceProjectIdFromApi: input.responseProjectId,
  });
}

export function uniqueProjectIds(scope: ProjectResourceScope): string[] {
  return Array.from(new Set([scope.permissionProjectId, scope.resourceProjectId]));
}

type ResourceElementType = 'character' | 'location' | 'prop';

function isResourceElementType(elementType: ElementType): elementType is ResourceElementType {
  return elementType === 'character' || elementType === 'location' || elementType === 'prop';
}

const STORYBOARD_ITEMS_ROOT = 'storyboard-items';

export type CancelElementTagUpdateInFlightQueriesParams = ProjectResourceScope & {
  tagId?: string;
  elementType: ElementType;
  elementId: string;
  storyboardId?: string;
};

export type CancelledElementTagUpdateQuery = {
  queryKey: QueryKey;
  queryHash: string;
  wasActive: boolean;
};

function isScopedElementTagQueryKey(
  queryKey: QueryKey,
  params: CancelElementTagUpdateInFlightQueriesParams,
  projectIds: ReadonlySet<string>
): boolean {
  return (
    queryKey[0] === PROJECT_ELEMENT_TAGS_ROOT &&
    typeof queryKey[1] === 'string' &&
    projectIds.has(queryKey[1]) &&
    (queryKey[2] === undefined || queryKey[2] === params.elementType) &&
    (queryKey[3] === undefined || queryKey[3] === params.elementId)
  );
}

function isScopedElementDetailQueryKey(
  queryKey: QueryKey,
  params: CancelElementTagUpdateInFlightQueriesParams,
  projectIds: ReadonlySet<string>
): boolean {
  if (!isResourceElementType(params.elementType)) return false;
  return uniqueProjectIds(params).some((projectId) => {
    if (!projectIds.has(projectId)) return false;
    const expectedKey =
      params.elementType === 'character'
        ? elementDetailKeys.character(projectId, params.elementId)
        : params.elementType === 'location'
          ? elementDetailKeys.location(projectId, params.elementId)
          : elementDetailKeys.prop(projectId, params.elementId);
    return (
      expectedKey.length === queryKey.length &&
      expectedKey.every((part, index) => part === queryKey[index])
    );
  });
}

function isScopedProjectElementListQueryKey(
  queryKey: QueryKey,
  params: CancelElementTagUpdateInFlightQueriesParams,
  projectIds: ReadonlySet<string>
): boolean {
  if (!isResourceElementType(params.elementType)) return false;
  const listPrefix =
    params.elementType === 'character'
      ? projectElementKeys.characters
      : params.elementType === 'location'
        ? projectElementKeys.locations
        : projectElementKeys.props;
  return uniqueProjectIds(params).some((projectId) => {
    if (!projectIds.has(projectId)) return false;
    const prefix = listPrefix(projectId);
    return prefix.every((part, index) => queryKey[index] === part);
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function cachedItemsContainTagId(
  data: unknown,
  field: 'elementTagId' | 'characterTagId',
  tagId: string
): boolean {
  if (!Array.isArray(data)) return false;
  return data.some((item) => {
    if (!isRecord(item)) return false;
    const candidate = item[field];
    return typeof candidate === 'string' && candidate.trim() === tagId;
  });
}

/** storyboard-items 的 key 不含 projectId；只 cancel 当前分镜，或缓存里已带这个 tagId 的查询。 */
function isScopedStoryboardItemsQueryKey(
  queryKey: QueryKey,
  data: unknown,
  params: CancelElementTagUpdateInFlightQueriesParams
): boolean {
  if (queryKey[0] !== STORYBOARD_ITEMS_ROOT) return false;
  if (params.storyboardId && queryKey[1] === params.storyboardId) return true;
  return Boolean(params.tagId && cachedItemsContainTagId(data, 'elementTagId', params.tagId));
}

/** dialogues 的 key 不含 projectId；只 cancel 当前分镜，或缓存里已带这个 characterTagId 的查询。 */
function isScopedStoryboardDialoguesQueryKey(
  queryKey: QueryKey,
  data: unknown,
  params: CancelElementTagUpdateInFlightQueriesParams
): boolean {
  if (params.elementType !== 'character') return false;
  if (queryKey[0] !== storyboardDialoguesKeys.all[0]) return false;
  if (params.storyboardId && queryKey[1] === params.storyboardId) return true;
  return Boolean(params.tagId && cachedItemsContainTagId(data, 'characterTagId', params.tagId));
}

function isElementTagUpdateQueryKey(
  queryKey: QueryKey,
  params: CancelElementTagUpdateInFlightQueriesParams,
  projectIds: ReadonlySet<string>,
  data?: unknown
): boolean {
  if (isScopedElementTagQueryKey(queryKey, params, projectIds)) return true;
  if (!isResourceElementType(params.elementType)) return false;

  return (
    (queryKey[0] === 'resource-library' &&
      queryKey[1] === 'v1' &&
      typeof queryKey[3] === 'string' &&
      projectIds.has(queryKey[3]) &&
      queryKey[4] === params.elementType) ||
    isTagResourcesQueryKey(queryKey, { tagId: params.tagId }) ||
    (queryKey[0] === storyboardBoardKeys.all[0] &&
      (queryKey[1] === 'list' || queryKey[1] === 'paginated') &&
      typeof queryKey[2] === 'string' &&
      projectIds.has(queryKey[2])) ||
    isScopedStoryboardItemsQueryKey(queryKey, data, params) ||
    isScopedStoryboardDialoguesQueryKey(queryKey, data, params) ||
    Boolean(
      params.storyboardId &&
      queryKey[0] === mentionKeys.all[0] &&
      queryKey[1] === params.storyboardId
    ) ||
    isScopedElementDetailQueryKey(queryKey, params, projectIds) ||
    isScopedProjectElementListQueryKey(queryKey, params, projectIds)
  );
}

/**
 * 标签更新成功后、写入即时补丁前，取消可能携带旧快照的 GET。
 * 接到现有双 projectId 口径，不回退 release 的独立失效路径。
 */
export async function cancelElementTagUpdateInFlightQueries(
  queryClient: QueryClient,
  params: CancelElementTagUpdateInFlightQueriesParams
): Promise<CancelledElementTagUpdateQuery[]> {
  const projectIds = new Set(uniqueProjectIds(params));
  const capturedByHash = new Map<string, CancelledElementTagUpdateQuery>();

  for (const query of queryClient.getQueryCache().findAll({
    predicate: (candidate) =>
      candidate.state.fetchStatus !== 'idle' &&
      isElementTagUpdateQueryKey(candidate.queryKey, params, projectIds, candidate.state.data),
  })) {
    capturedByHash.set(query.queryHash, {
      queryKey: query.queryKey,
      queryHash: query.queryHash,
      wasActive: query.getObserversCount() > 0,
    });
  }

  const capturedQueries = Array.from(capturedByHash.values());
  await Promise.allSettled(
    capturedQueries.map((query) =>
      queryClient.cancelQueries({ queryKey: query.queryKey, exact: true })
    )
  );

  return capturedQueries;
}

export async function reconcileElementTagUpdateQueries(
  queryClient: QueryClient,
  params: CancelElementTagUpdateInFlightQueriesParams & {
    cancelledQueries: readonly CancelledElementTagUpdateQuery[];
  }
): Promise<void> {
  const queriesToRefetch = new Map<string, QueryKey>();
  for (const captured of params.cancelledQueries) {
    if (!captured.wasActive) continue;
    const query = queryClient.getQueryCache().get(captured.queryHash);
    if (query && query.state.data === undefined && query.getObserversCount() > 0) {
      queriesToRefetch.set(query.queryHash, query.queryKey);
    }
  }

  await Promise.allSettled(
    Array.from(queriesToRefetch.values(), (queryKey) =>
      queryClient.refetchQueries({ queryKey, exact: true })
    )
  );
}

export function syncUpdatedProjectElementTagCaches(
  queryClient: QueryClient,
  result: ProjectElementTagResponse
): void {
  queryClient.setQueriesData<ProjectElementTagResponse[]>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === PROJECT_ELEMENT_TAGS_ROOT &&
        Array.isArray(query.state.data),
    },
    (current) =>
      current?.map((tag) =>
        tag.tagId === result.tagId ? mergeUpdatedElementTag(tag, result) : tag
      ) ?? current
  );
}

/** 跨 permission / resource projectId 失效同一元素的 element-tags 缓存 */
export function invalidateElementTagQueriesForElement(
  queryClient: QueryClient,
  elementType: ElementType,
  elementId: string,
  refetchType: 'active' | 'none' = 'none'
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === PROJECT_ELEMENT_TAGS_ROOT &&
        key[2] === elementType &&
        key[3] === elementId
      );
    },
    refetchType,
  });
}

export type InvalidateElementTagMutationCachesParams = ProjectResourceScope & {
  tagId?: string;
  elementType: ElementType;
  elementId: string;
  storyboardId?: string;
  result?: ProjectElementTagResponse;
  elementTagsRefetchType?: QueryRefetchType;
  insertResultIfMissing?: boolean;
  tagResourcesRefetchType?: QueryRefetchType;
  projectElementsListRefetchType?: QueryRefetchType;
  resourceLibraryRefetchType?: QueryRefetchType;
};

/** 定稿素材 / 封面 / 标签更新后的统一缓存失效（双 projectId + element 谓词兜底） */
export function invalidateElementTagMutationCaches(
  queryClient: QueryClient,
  params: InvalidateElementTagMutationCachesParams
): void {
  const { tagId, elementType, elementId, storyboardId } = params;
  const elementTagsRefetchType = params.elementTagsRefetchType ?? 'active';
  const tagResourcesRefetchType = params.tagResourcesRefetchType ?? 'active';
  const shouldRefreshElementPresentation =
    params.result?.isDefault === true && isResourceElementType(params.result.elementType);
  const projectElementsListRefetchType = params.projectElementsListRefetchType ?? 'none';
  const resourceLibraryRefetchType =
    params.resourceLibraryRefetchType ?? (shouldRefreshElementPresentation ? 'active' : 'none');
  const scope: ProjectResourceScope = {
    permissionProjectId: params.permissionProjectId,
    resourceProjectId: params.resourceProjectId,
  };
  const projectIds = uniqueProjectIds(scope);

  if (params.result) {
    patchElementTagCaches(queryClient, params.result, {
      projectIds,
      insertIfMissing: params.insertResultIfMissing,
    });
  }

  for (const projectId of projectIds) {
    void queryClient.invalidateQueries({
      queryKey: elementTagsProjectKey(projectId),
      refetchType: 'none',
    });
    void queryClient.invalidateQueries({
      queryKey: elementTagsElementKey(projectId, elementType, elementId),
      refetchType: 'none',
    });

    if (isResourceElementType(elementType)) {
      markElementDetailStale(queryClient, projectId, elementType, elementId);
      markProjectElementsListStale(
        queryClient,
        projectId,
        elementType,
        projectElementsListRefetchType
      );
      markResourceLibraryStale(queryClient, projectId, elementType, resourceLibraryRefetchType);
    }
  }

  invalidateElementTagQueriesForElement(
    queryClient,
    elementType,
    elementId,
    elementTagsRefetchType
  );

  if (tagId) {
    void queryClient.invalidateQueries({
      predicate: (query) => isTagResourcesQueryKey(query.queryKey, { tagId }),
      refetchType: tagResourcesRefetchType,
    });
  }

  invalidateMentions(queryClient);

  if (storyboardId) {
    invalidateStoryboardDialogues(queryClient, storyboardId);
  }
}

/** 设置默认标签后的缓存失效（无 tagId；双 projectId + element 谓词） */
export function invalidateDefaultTagMutationCaches(
  queryClient: QueryClient,
  params: ProjectResourceScope & {
    elementType: ElementType;
    elementId: string;
  }
): void {
  const scope: ProjectResourceScope = {
    permissionProjectId: params.permissionProjectId,
    resourceProjectId: params.resourceProjectId,
  };
  const { elementType, elementId } = params;

  for (const projectId of uniqueProjectIds(scope)) {
    void queryClient.invalidateQueries({
      queryKey: elementTagsProjectKey(projectId),
      refetchType: 'active',
    });
    void queryClient.invalidateQueries({
      queryKey: elementTagsElementKey(projectId, elementType, elementId),
      refetchType: 'none',
    });

    if (isResourceElementType(elementType)) {
      markElementDetailStale(queryClient, projectId, elementType, elementId);
      markProjectElementsListStale(queryClient, projectId, elementType, 'none');
      markResourceLibraryStale(queryClient, projectId, elementType, 'active');
    }
  }

  invalidateElementTagQueriesForElement(queryClient, elementType, elementId, 'active');
  invalidateMentions(queryClient);
}

/** 创建元素标签后的统一缓存失效 */
export function invalidateElementTagCreatedCaches(
  queryClient: QueryClient,
  params: {
    permissionProjectId: string;
    result: ProjectElementTagResponse;
    elementTagsRefetchType?: QueryRefetchType;
  }
): void {
  const { result } = params;
  invalidateElementTagMutationCaches(queryClient, {
    permissionProjectId: params.permissionProjectId,
    resourceProjectId: result.projectId,
    tagId: result.tagId,
    elementType: result.elementType,
    elementId: result.elementId,
    result,
    elementTagsRefetchType: params.elementTagsRefetchType,
    insertResultIfMissing: true,
  });
}

/** 删除元素标签后的统一缓存失效 */
export function invalidateElementTagDeletedCaches(
  queryClient: QueryClient,
  params: ProjectResourceScope & {
    tagId: string;
    elementType: ElementType;
    elementId: string;
    wasDefaultTag?: boolean;
  }
): void {
  const scope: ProjectResourceScope = {
    permissionProjectId: params.permissionProjectId,
    resourceProjectId: params.resourceProjectId,
  };
  removeElementTagCaches(queryClient, {
    projectIds: uniqueProjectIds(scope),
    tagId: params.tagId,
    elementType: params.elementType,
    elementId: params.elementId,
  });

  invalidateElementTagMutationCaches(queryClient, {
    permissionProjectId: params.permissionProjectId,
    resourceProjectId: params.resourceProjectId,
    tagId: params.tagId,
    elementType: params.elementType,
    elementId: params.elementId,
    tagResourcesRefetchType: 'none',
    projectElementsListRefetchType: 'none',
    resourceLibraryRefetchType: params.wasDefaultTag ? 'active' : 'none',
  });
}

/** AI 角色/场景/道具生图完成后的缓存失效（双 projectId） */
export async function invalidateResourceImageTaskCaches(
  queryClient: QueryClient,
  params: ProjectResourceScope & {
    mode: ResourceImageTaskMode;
    resourceId: string;
    tagId?: string | null;
    storyboardId?: string | null;
    refreshElementPresentation?: boolean;
  }
): Promise<boolean> {
  const { mode, resourceId, tagId, storyboardId } = params;
  const scope: ProjectResourceScope = {
    permissionProjectId: params.permissionProjectId,
    resourceProjectId: params.resourceProjectId,
  };

  const resourceLibraryTypeByMode: Record<ResourceImageTaskMode, ResourceLibraryType> = {
    'character-image': 'character',
    'location-image': 'location',
    'prop-image': 'prop',
  };
  const elementTypeByMode: Record<ResourceImageTaskMode, ResourceElementType> = {
    'character-image': 'character',
    'location-image': 'location',
    'prop-image': 'prop',
  };
  const resourceLibraryType = resourceLibraryTypeByMode[mode];
  const elementType = elementTypeByMode[mode];
  const presentationRefetchType: QueryRefetchType = params.refreshElementPresentation
    ? 'active'
    : 'none';
  const sharedInvalidations: Promise<unknown>[] = [];

  for (const projectId of uniqueProjectIds(scope)) {
    sharedInvalidations.push(
      queryClient.invalidateQueries({
        queryKey: elementTagsProjectKey(projectId),
        refetchType: 'none',
      })
    );
    markResourceLibraryStale(queryClient, projectId, resourceLibraryType, presentationRefetchType);
    invalidateResourceLibraryTagResources(queryClient, projectId, tagId ?? undefined);
  }

  if (storyboardId) {
    invalidateMentionQueriesForStoryboard(queryClient, storyboardId);
  }

  for (const projectId of uniqueProjectIds(scope)) {
    markElementDetailStale(queryClient, projectId, elementType, resourceId);
    markProjectElementsListStale(queryClient, projectId, elementType, 'none');
  }

  invalidateElementTagQueriesForElement(
    queryClient,
    elementType,
    resourceId,
    presentationRefetchType
  );
  invalidateMentions(queryClient);

  await Promise.all(sharedInvalidations);
  return true;
}
